import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  semanticGapService,
  type SemanticGap,
  type ConsolidationRun,
} from '../services/semanticGap.service';

type ListRecordsResponse = {
  records: SemanticGap[];
  total_records?: number;
  total_pages?: number;
};

export function useSemanticGaps(options?: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
  enabled?: boolean;
}) {
  const { page = 1, pageSize = 1000, filters = {}, enabled = true } = options || {};

  return useQuery<ListRecordsResponse>({
    queryKey: ['semanticGaps', page, pageSize, filters],
    queryFn: () => semanticGapService.find({ page, pageSize, filters }),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useSemanticGapById(id: string | null | undefined) {
  return useQuery<SemanticGap>({
    queryKey: ['semanticGap', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Gap ID is required'));
      return semanticGapService.findById(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useResolveSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useDismissSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useApproveSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useDeleteSemanticGap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => semanticGapService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
    },
  });
}

export function useStartConsolidation() {
  return useMutation({
    mutationFn: (force: boolean = false) =>
      semanticGapService.startConsolidation(force),
  });
}

export function useConsolidationRun(runId: string | null | undefined) {
  return useQuery<ConsolidationRun>({
    queryKey: ['consolidationRun', runId],
    queryFn: () => {
      if (!runId) return Promise.reject(new Error('Run ID is required'));
      return semanticGapService.getConsolidationRun(runId);
    },
    enabled: !!runId,
    // Poll while the LLM job is still running.
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? 2000 : false,
  });
}

export function useApplyConsolidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => semanticGapService.applyConsolidation(runId),
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: ['semanticGaps'] });
      queryClient.setQueryData(['consolidationRun', run.id], run);
    },
  });
}

export function useRejectConsolidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => semanticGapService.rejectConsolidation(runId),
    onSuccess: (run) => {
      queryClient.setQueryData(['consolidationRun', run.id], run);
    },
  });
}
